import { useCallback, useEffect, useState } from "react";
import { ContractorService } from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";

export const useContractorData = (contractorId: number) => {
  const [contractorData, setContractorData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // True specifically when the contractor no longer exists (404 -- e.g. deleted). Distinct from
  // `error`, a transient failure (server down, network error) that must NOT show a goodbye screen.
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  const fetchContractorData = useCallback(async () => {
    // No contractorId ever resolved -- e.g. a deleted contractor's login succeeds (see
    // LoginController.BuildJwtToken) but issues no ContractorId claim, so sessionStorage never
    // gets a real id. Must still clear `loading`, or the dashboard spins forever instead of
    // reaching the goodbye screen.
    if (!contractorId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setNotFound(false);
      setError(false);
      const res: any = await apiHelper(
        ContractorService.getApiContractor,
        { showNotification: false },
        contractorId
      );
      // apiHelper never throws -- it catches internally and always resolves, returning
      // `data: null` on any failure. statusCode is only reliably populated for the failure path.
      if (res?.data == null) {
        if (res?.statusCode === 404) {
          setNotFound(true);
        } else {
          setError(true);
        }
      } else {
        setContractorData(res.data.success ?? null);
      }
    } catch (err) {
      console.error("Error fetching contractor data:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => {
    fetchContractorData();
  }, [fetchContractorData]);

  return {
    contractorData,
    loading,
    notFound,
    error,
    refetchContractorData: fetchContractorData,
  };
};
